import { inject, Injectable } from '@angular/core';
import {
  getDownloadURL,
  getMetadata,
  ref,
  Storage,
} from '@angular/fire/storage';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { DialogPresets } from '@principle-theorem/ng-shared';
import {
  FundingRequest,
  isMohClaimFundingRequest,
  isMohPreApprovalFundingRequest,
  MohClaimFundingRequest,
  MohPreApprovalFundingRequest,
  Patient,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IMedia,
  IMohSendEnrolmentFormRequest,
  IPatient,
  IPractice,
  MohFundingRequest,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  Firestore,
  getError,
  httpsCallable,
  IAttachment,
  serialise,
  SerialisedData,
  snapshot,
  snapshotDefined,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import {
  IMohPatientAttachmentsDialogData,
  MohPatientAttachmentsDialogComponent,
} from '../components/moh/moh-patient-attachments-dialog/moh-patient-attachments-dialog.component';
import { MohPracticeSettingsDialogComponent } from '../components/moh/moh-practice-settings-dialog/moh-practice-settings-dialog.component';
import {
  IMohSignEnrolmentFormDialogData,
  MohSignEnrolmentFormDialogComponent,
  MohSignEnrolmentFormForm,
} from '../components/moh/moh-generate-enrolment-form-dialog/moh-sign-enrolment-form-dialog/moh-sign-enrolment-form-dialog.component';
import {
  IMohGenerateEnrolmentDialogData,
  MohGenerateEnrolmentFormDialogComponent,
} from '../components/moh/moh-generate-enrolment-form-dialog/moh-generate-enrolment-form-dialog.component';
import { MohViewFormsDialogComponent } from '../components/moh/moh-view-forms-dialog/moh-view-forms-dialog.component';

@Injectable()
export class MohService {
  private _dialog = inject(MatDialog);
  private _storage = inject(Storage);
  private _org = inject(OrganisationService);
  private _snackBar = inject(MatSnackBar);

  viewForms(claim: WithRef<MohFundingRequest>): void {
    const forms = isMohClaimFundingRequest(claim)
      ? MohClaimFundingRequest.formDisplays(claim)
      : isMohPreApprovalFundingRequest(claim)
        ? MohPreApprovalFundingRequest.formDisplays(claim)
        : [];
    this._dialog.open(
      MohViewFormsDialogComponent,
      DialogPresets.extraLarge({ data: forms })
    );
  }

  async addPatientMediaAttachments(
    fundingRequest: WithRef<MohFundingRequest>
  ): Promise<void> {
    const patient = await Firestore.getDoc(fundingRequest.patient.ref);
    const patientMedia = await Firestore.getDocs(Patient.mediaCol(patient));

    const media = await this._dialog
      .open<
        MohPatientAttachmentsDialogComponent,
        IMohPatientAttachmentsDialogData,
        WithRef<IMedia>[]
      >(
        MohPatientAttachmentsDialogComponent,
        DialogPresets.extraLarge({
          data: { patient, media: patientMedia },
        })
      )
      .afterClosed()
      .toPromise();
    if (!media) {
      return;
    }

    await asyncForEach(media, (mediaItem) =>
      this._addPatientMediaAttachment(fundingRequest, mediaItem)
    );
  }

  async validatePracticeMohSettings(
    practice: WithRef<IPractice>
  ): Promise<boolean> {
    if (Practice.hasValidMohSettings(practice)) {
      return true;
    }

    const settingsSaved = await this._dialog
      .open<
        MohPracticeSettingsDialogComponent,
        WithRef<IPractice>,
        WithRef<IPractice>
      >(
        MohPracticeSettingsDialogComponent,
        DialogPresets.large({ data: practice })
      )
      .afterClosed()
      .toPromise();
    return !!settingsSaved;
  }

  async sendEnrolmentForm(patient: WithRef<IPatient>): Promise<void> {
    const practice = await snapshotDefined(this._org.practice$);
    const patientMobileNumber = await Patient.getMobileNumber(patient);
    const formPath = await this._dialog
      .open<
        MohGenerateEnrolmentFormDialogComponent,
        IMohGenerateEnrolmentDialogData,
        string
      >(
        MohGenerateEnrolmentFormDialogComponent,
        DialogPresets.extraLarge({
          data: {
            practice,
            patient,
            patientMobileNumber,
          },
        })
      )
      .afterClosed()
      .toPromise();
    if (!formPath) {
      this._snackBar.open('Failed to generate enrolment form');
      return;
    }

    const signatureData = await this._dialog
      .open<
        MohSignEnrolmentFormDialogComponent,
        IMohSignEnrolmentFormDialogData,
        MohSignEnrolmentFormForm
      >(
        MohSignEnrolmentFormDialogComponent,
        DialogPresets.almostFullscreen({
          data: { practice, patient, formPath },
        })
      )
      .afterClosed()
      .toPromise();
    if (!signatureData) {
      return;
    }
    const request: IMohSendEnrolmentFormRequest = {
      ...signatureData,
      patient: patient.ref,
      practice: practice.ref,
      formPath,
    };

    const formSent = await snapshot(
      httpsCallable<SerialisedData<IMohSendEnrolmentFormRequest>, string>(
        'http-moh-sendEnrolmentForm'
      )(serialise(request))
    );
    if (!formSent) {
      this._snackBar.open('Error sending MOH enrolment form');
      return;
    }

    await Firestore.patchDoc(patient.ref, {
      metadata: {
        ...patient.metadata,
        moh: {
          ...patient.metadata?.moh,
          enrolmentFormPath: formPath,
          enrolmentFormSentAt: toTimestamp(moment()),
        },
      },
    });

    this._snackBar.open('MOH enrolment form sent successfully');
  }

  private async _addPatientMediaAttachment(
    fundingRequest: WithRef<MohFundingRequest>,
    media: WithRef<IMedia>
  ): Promise<void> {
    try {
      const fileRef = ref(this._storage, media.path);
      const { size, name, contentType, fullPath, updated } =
        await getMetadata(fileRef);
      const link = await getDownloadURL(fileRef);
      const attachment: IAttachment = {
        link,
        size,
        name,
        type: contentType || 'application/octet-stream',
        lastModified: new Date(updated).getTime(),
        path: fullPath,
        deleted: false,
      };

      await FundingRequest.addAttachment(
        { ref: fundingRequest.ref },
        attachment
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
    }
  }
}
